/**
 * 로그 설정(§7.3): 기록 켜기, 인자 기록(기본 꺼짐, 켤 때 확인), 파일 상한, 보존 기간.
 */
import { Body1, Caption1, makeStyles, Switch, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar } from "@/components/app-dialog";
import { FuiDropdown } from "@/components/fui-control";
import { invoke } from "@/desktop/runtime";

export interface LogConfig {
  enabled: boolean;
  maxFileBytes: number;
  recordArgs: boolean;
  retentionDays: number;
}

const MIB = 1024 * 1024;
const SIZE_OPTIONS = [1, 5, 10, 20].map((n) => ({ value: String(n * MIB), label: `${n} MB × 3개` }));
const RETENTION_OPTIONS = [7, 30, 90, 365].map((n) => ({ value: String(n), label: `${n}일` }));

const useStyles = makeStyles({
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalL, minHeight: "32px" },
  text: { display: "flex", flexDirection: "column", minWidth: 0 },
  label: { flexShrink: 0, whiteSpace: "nowrap" },
  select: { width: "168px", minWidth: "168px", maxWidth: "168px", flexShrink: 0 },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorStatusDangerForeground1 },
});

export function LogSettingsDialog({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const styles = useStyles();
  const [config, setConfig] = useState<LogConfig | null>(null);
  const [confirmArgs, setConfirmArgs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    void invoke<LogConfig>("get_log_settings").then(setConfig, () => setError("설정을 읽지 못했습니다"));
  }, [open]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await invoke("update_log_settings", { config });
      onSaved();
      onClose();
    } catch {
      setError("설정을 저장하지 못했습니다");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog open={open} onClose={onClose} title="로그 설정" width="sm" footer={<DialogActionsBar onCancel={onClose} onConfirm={save} disabled={!config || saving} confirmLabel={saving ? "저장 중…" : "저장"} />}>
      {config && (
        <>
          <div className={styles.row}>
            <div className={styles.text}>
              <Body1>로그 기록</Body1>
              <Caption1 className={styles.hint}>끄면 새 기록을 파일에 쓰지 않습니다</Caption1>
            </div>
            <Switch checked={config.enabled} onChange={(_, d) => setConfig({ ...config, enabled: d.checked })} aria-label="로그 기록" />
          </div>
          <div className={styles.row}>
            <div className={styles.text}>
              <Body1>도구 인자 기록</Body1>
              <Caption1 className={styles.hint}>켜면 인자 값을 마스킹해 남깁니다. 기본은 크기·해시만</Caption1>
            </div>
            <Switch
              checked={config.recordArgs}
              onChange={(_, d) => (d.checked ? setConfirmArgs(true) : setConfig({ ...config, recordArgs: false }))}
              aria-label="도구 인자 기록"
            />
          </div>
          <div className={styles.row}>
            <Body1 className={styles.label}>파일 크기 상한</Body1>
            <FuiDropdown className={styles.select} density="dialog" value={String(config.maxFileBytes)} options={SIZE_OPTIONS} onValueChange={(v) => setConfig({ ...config, maxFileBytes: Number(v) })} aria-label="파일 크기 상한" />
          </div>
          <div className={styles.row}>
            <Body1 className={styles.label}>보존 기간</Body1>
            <FuiDropdown className={styles.select} density="dialog" value={String(config.retentionDays)} options={RETENTION_OPTIONS} onValueChange={(v) => setConfig({ ...config, retentionDays: Number(v) })} aria-label="보존 기간" />
          </div>
        </>
      )}
      {error && (
        <Caption1 className={styles.error} role="alert">
          {error}
        </Caption1>
      )}
      <ConfirmDialog
        open={confirmArgs}
        title="도구 인자 기록"
        message="「도구 인자 기록」을 켤까요? 작업명·코멘트 같은 검색어와 인자가 로그 파일에 남습니다."
        confirmLabel="켜기"
        onCancel={() => setConfirmArgs(false)}
        onConfirm={() => {
          if (config) setConfig({ ...config, recordArgs: true });
          setConfirmArgs(false);
        }}
      />
    </AppDialog>
  );
}
